import {styled} from '@mui/material/styles'
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormControl,
  Chip,
  IconButton,
  Divider,
  Paper,
  Alert,
  Dialog,
  Avatar,
  Typography,
  Card,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'

// ─── Color Tokens ───────────────────────────────────────────────────────────
const palette = {
  primary: '#1565c0',
  primaryLight: '#e3f2fd',
  primaryBorder: '#90caf9',
  success: '#2e7d32',
  successLight: '#e8f5e9',
  successBorder: '#a5d6a7',
  info: '#0288d1',
  infoLight: '#e1f5fe',
  infoBorder: '#81d4fa',
  error: '#d32f2f',
  errorLight: '#ffebee',
  errorBorder: '#ef9a9a',
  warning: '#ed6c02',
  warningLight: '#fff3e0',
  warningBorder: '#ffcc80',
  grey50: '#fafafa',
  grey100: '#f5f5f5',
  grey200: '#eeeeee',
  grey300: '#e0e0e0',
  grey600: '#757575',
  grey800: '#424242',
  white: '#ffffff',
  shadow: 'rgba(0, 0, 0, 0.05)',
  shadowMd: 'rgba(0, 0, 0, 0.08)',
}

// ─── Layout ─────────────────────────────────────────────────────────────────
export const MainContainer = styled(Box)({
  width: '100%',
})

export const RulesListContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}))

// ─── Page Header / Action Bar ───────────────────────────────────────────────
export const ActionBar = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2.5),
  gap: theme.spacing(2),
}))

export const ActionBarLeft = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  flexGrow: 1,
}))

export const AddRuleButton = styled(Button)(({theme}) => ({
  borderRadius: 10,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: theme.spacing(1, 2.5),
  borderColor: palette.primaryBorder,
  color: palette.primary,
  '&:hover': {
    borderColor: palette.primary,
    backgroundColor: palette.primaryLight,
  },
}))

export const DownloadButton = styled(Button)(({theme}) => ({
  borderRadius: 10,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: theme.spacing(1, 2.5),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}))

export const ConfirmAllocationButton = styled(Button)(({theme}) => ({
  borderRadius: 10,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: theme.spacing(1, 2.5),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: `0 4px 12px rgba(211, 47, 47, 0.3)`,
  },
}))

// ─── Alert Banner ───────────────────────────────────────────────────────────
export const AlertContainer = styled(Alert)(({theme}) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(2.5),
  '& .MuiAlert-message': {
    fontSize: '0.875rem',
  },
}))

// ─── Stats Section ──────────────────────────────────────────────────────────
export const StatsGridContainer = styled(Grid)(({theme}) => ({
  marginBottom: theme.spacing(3),
}))

export const StatsCard = styled(Paper)(({theme}) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
}))

export const StatsContentContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}))

export const StatsAvatar = styled(Avatar)({
  width: 48,
  height: 48,
  borderRadius: 14,
})

export const StatsTextGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

export const StatsNumberText = styled(Typography)({
  fontWeight: 800,
  fontSize: '1.75rem',
  lineHeight: 1.2,
})

export const StatsLabelText = styled(Typography)({
  fontSize: '0.8rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
})

// ─── Focus Overlay ─────────────────────────────────────────────────────────
export const FocusOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.35)',
  zIndex: 10,
  pointerEvents: 'none',
})

// ─── Rule Card / Accordion ──────────────────────────────────────────────────
export const RuleCardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFocused',
})(({isFocused}) => ({
  position: 'relative',
  zIndex: isFocused ? 11 : 'auto',
}))

export const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== 'accentColor',
})(({accentColor}) => ({
  borderRadius: '14px !important',
  border: `1px solid ${palette.grey200}`,
  borderLeft: `4px solid ${accentColor || palette.grey300}`,
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    boxShadow: 'none',
  },
  overflow: 'hidden',
}))

export const StyledAccordionSummary = styled(AccordionSummary)(({theme}) => ({
  padding: theme.spacing(0, 2),
  minHeight: 64,
  '&.Mui-expanded': {
    minHeight: 64,
    borderBottom: `1px solid ${palette.grey200}`,
  },
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1.25, 0),
    '&.Mui-expanded': {
      margin: theme.spacing(1.25, 0),
    },
  },
}))

export const StyledAccordionDetails = styled(AccordionDetails)(({theme}) => ({
  padding: theme.spacing(3),
  backgroundColor: palette.grey50,
}))

export const AccordionContentContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  paddingRight: theme.spacing(1),
}))

export const LeftContentContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flex: 1,
  minWidth: 0,
}))

export const PriorityNumberBox = styled(Box)({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: palette.grey100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${palette.grey300}`,
  flexShrink: 0,
})

export const PriorityNumberText = styled(Typography)({
  fontWeight: 700,
  color: palette.grey800,
  fontSize: '0.8rem',
})

export const RuleInfoGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
})

export const RuleNameText = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.9rem',
  color: palette.grey800,
  lineHeight: 1.3,
})

export const RuleMetaRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: 4,
}))

const metaChipColors = {
  // Shipment
  B2C: {bg: palette.primaryLight, color: palette.primary, border: palette.primaryBorder},
  B2B: {bg: '#fff3e0', color: '#e65100', border: '#ffcc80'},
  // Payment
  COD: {bg: '#fce4ec', color: '#c62828', border: '#ef9a9a'},
  Prepaid: {bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7'},
  // QC
  QC: {bg: '#e1f5fe', color: '#0277bd', border: '#81d4fa'},
  'Non-QC': {bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8'},
  // Dispatch
  Surface: {bg: '#efebe9', color: '#4e342e', border: '#bcaaa4'},
  Air: {bg: '#e8eaf6', color: '#283593', border: '#9fa8da'},
  'Surface & Air': {bg: '#ede7f6', color: '#4527a0', border: '#b39ddb'},
}
const defaultMetaChipColor = {bg: palette.grey100, color: palette.grey600, border: palette.grey200}

export const MetaChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'chipValue',
})(({chipValue}) => {
  const c = metaChipColors[chipValue] || defaultMetaChipColor
  return {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 600,
    borderRadius: 4,
    backgroundColor: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`,
    '& .MuiChip-label': {
      paddingLeft: 6,
      paddingRight: 6,
    },
  }
})

export const DeliveryTypeBadge = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({variant}) => {
  const colors = {
    FORWARD: {bg: palette.successLight, color: palette.success, border: palette.successBorder},
    RVP: {bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8'},
  }
  const c = colors[variant] || colors.FORWARD
  return {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderRadius: 4,
    backgroundColor: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`,
    '& .MuiChip-label': {
      paddingLeft: 6,
      paddingRight: 6,
    },
  }
})

export const ActionsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flexShrink: 0,
}))

export const ActionButtonsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
}))

export const MoveButton = styled(IconButton)({
  width: 28,
  height: 28,
  borderRadius: 8,
  color: palette.grey600,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: palette.grey100,
    color: palette.grey800,
  },
  '&.Mui-disabled': {
    color: palette.grey300,
  },
})

export const DeleteButton = styled(IconButton)({
  width: 28,
  height: 28,
  borderRadius: 8,
  color: palette.grey600,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: palette.errorLight,
    color: palette.error,
  },
})

// ─── Accordion Detail Sections ──────────────────────────────────────────────
export const SectionDivider = styled(Divider)(({theme}) => ({
  margin: theme.spacing(0.5, 0),
}))

export const SectionHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}))

export const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.95rem',
  color: palette.grey800,
})

export const SectionSubtitle = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: palette.grey600,
  display: 'block',
  marginTop: 4,
})

// ─── Auto Allocation Cards ──────────────────────────────────────────────────
export const AllocationCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'accentColor',
})(({isSelected, accentColor}) => ({
  padding: 16,
  cursor: 'pointer',
  borderRadius: 12,
  border: isSelected ? `2px solid ${accentColor}` : `1px solid ${palette.grey200}`,
  backgroundColor: isSelected ? `${accentColor}08` : palette.white,
  boxShadow: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: accentColor,
  },
}))

export const AllocationCardHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.75),
}))

export const AllocationCardLabel = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.875rem',
})

export const AllocationCardDescription = styled(Typography)({
  fontSize: '0.75rem',
  color: palette.grey600,
  lineHeight: 1.4,
})

// ─── Advanced Settings ──────────────────────────────────────────────────────
export const AdvancedSettingsContainer = styled(Box)(({theme}) => ({
  backgroundColor: palette.white,
  borderRadius: 12,
  border: `1px solid ${palette.grey200}`,
  padding: theme.spacing(2.5),
}))

export const FieldHelperText = styled(Typography)({
  fontSize: '0.7rem',
  color: palette.grey600,
  display: 'block',
  marginTop: 4,
})

export const PincodeManualBox = styled(Box)(({theme}) => ({
  marginTop: theme.spacing(1.5),
}))

// ─── Dialogs ────────────────────────────────────────────────────────────────
export const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    minWidth: 440,
  },
})

export const StyledDialogTitle = styled(DialogTitle)(({theme}) => ({
  fontSize: '1.15rem',
  fontWeight: 700,
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${palette.grey200}`,
}))

export const StyledDialogContent = styled(DialogContent)(({theme}) => ({
  padding: theme.spacing(3),
}))

export const StyledDialogActions = styled(DialogActions)(({theme}) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${palette.grey200}`,
  gap: theme.spacing(1),
}))

export const DialogFormContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}))

export const FormRow = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(2),
}))

export const FormField = styled(FormControl)({
  flex: 1,
})

export const DialogDividerRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing(2),
}))

export const DialogDividerLine = styled(Divider)({
  flex: 1,
})

export const DialogDividerText = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: palette.grey600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
})

export const DialogCancelButton = styled(Button)(({theme}) => ({
  borderRadius: 10,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(0.75, 2.5),
}))

export const DialogConfirmButton = styled(Button)(({theme}) => ({
  borderRadius: 10,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(0.75, 2.5),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}))

// ─── Delete Dialog ──────────────────────────────────────────────────────────
export const DeleteDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    maxWidth: 420,
  },
})

export const DeleteDialogIconBox = styled(Box)({
  width: 56,
  height: 56,
  borderRadius: 16,
  backgroundColor: palette.errorLight,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: 16,
})

export const DeleteDialogTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.15rem',
  textAlign: 'center',
  marginBottom: 8,
})

export const DeleteDialogBody = styled(Typography)({
  fontSize: '0.875rem',
  color: palette.grey600,
  textAlign: 'center',
  lineHeight: 1.6,
})

// ─── Validation Dialog ──────────────────────────────────────────────────────
export const ValidationDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: 16,
  },
})

export const ValidationDialogTitleBox = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}))

export const ValidationRuleItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'severity',
})(({severity = 'warning'}) => {
  const colors = {
    warning: {border: palette.warningBorder, bg: palette.warningLight},
    error: {border: palette.errorBorder, bg: palette.errorLight},
  }
  const c = colors[severity] || colors.warning
  return {
    padding: 14,
    marginBottom: 8,
    border: `1px solid ${c.border}`,
    borderRadius: 10,
    backgroundColor: c.bg,
  }
})

export const ValidationRuleName = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.875rem',
})

export const ValidationRuleDetail = styled(Typography)({
  fontSize: '0.8rem',
  color: palette.grey600,
  marginTop: 2,
})

export const ValidationRuleError = styled(Typography)({
  fontSize: '0.8rem',
  color: palette.error,
  marginTop: 4,
  fontWeight: 500,
})

// ─── Courier Selector ───────────────────────────────────────────────────────
export const CourierSelectorBox = styled(Box)({})

export const SelectAllOption = styled(Box)({
  borderBottom: `1px solid ${palette.grey200}`,
  backgroundColor: palette.grey50,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: palette.grey100,
  },
})

export const SelectAllText = styled(Typography)({
  fontWeight: 600,
  color: palette.primary,
  fontSize: '0.875rem',
})

// ─── Fallback Couriers Section ──────────────────────────────────────────────
export const FallbackCouriersContainer = styled(Box)(({theme}) => ({
  backgroundColor: palette.white,
  borderRadius: 12,
  border: `1px solid ${palette.grey200}`,
  padding: theme.spacing(2.5),
}))

// ─── Index / Wrapper ────────────────────────────────────────────────────────
export const WrapperCard = styled(Card)(({theme}) => ({
  marginTop: theme.spacing(1.5),
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
}))

export const TabContainer = styled(Box)({
  borderBottom: `1px solid ${palette.grey200}`,
})

export const EmptyStateContainer = styled(Box)(({theme}) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: palette.grey50,
}))

export const EmptyStateIconStyled = styled('div')({
  fontSize: 48,
  color: palette.grey300,
  marginBottom: 12,
  display: 'flex',
  justifyContent: 'center',
})

export const EmptyStateText = styled(Typography)({
  color: palette.grey600,
  fontSize: '0.95rem',
})

export const RulesContainer = styled(Box)({
  '& .MuiCard-root': {
    borderRadius: 0,
    border: 0,
  },
})

export const BuildTabContent = styled(Box)(({theme}) => ({
  padding: theme.spacing(2),
}))
